/**
 * Session storage utility functions for secure email verification handling
 */

const SESSION_KEYS = {
  PENDING_VERIFICATION_EMAIL: 'pendingVerificationEmail',
} as const;

/**
 * Stores email for pending verification in session storage
 * @param email - The email address to store
 */
export const storePendingVerificationEmail = (email: string): void => {
  try {
    if (!email || typeof email !== 'string') {
      console.warn('Invalid email provided for session storage');
      return;
    }
    
    // Sanitize email before storing
    const sanitizedEmail = email.trim().toLowerCase();
    sessionStorage.setItem(SESSION_KEYS.PENDING_VERIFICATION_EMAIL, sanitizedEmail);
  } catch (error) {
    console.error('Error storing pending verification email:', error);
  }
};

/**
 * Retrieves pending verification email from session storage
 * @returns The stored email or null if not found
 */
export const getPendingVerificationEmail = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_KEYS.PENDING_VERIFICATION_EMAIL);
  } catch (error) {
    console.error('Error retrieving pending verification email:', error);
    return null;
  }
};

/**
 * Removes pending verification email from session storage
 */
export const clearPendingVerificationEmail = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEYS.PENDING_VERIFICATION_EMAIL);
  } catch (error) {
    console.error('Error clearing pending verification email:', error);
  }
};

/**
 * Clears all session storage data related to email verification
 */
export const clearAllEmailVerificationData = (): void => {
  try {
    clearPendingVerificationEmail();
    // Add any other email verification related keys here
  } catch (error) {
    console.error('Error clearing email verification data:', error);
  }
};

/**
 * Checks if there is pending verification email in session storage
 * @returns True if pending verification email exists
 */
export const hasPendingVerificationEmail = (): boolean => {
  return getPendingVerificationEmail() !== null;
};

/**
 * Validates email format before storing
 * @param email - The email to validate
 * @returns True if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const trimmedEmail = email.trim();
  
  // Additional checks for common invalid patterns
  if (trimmedEmail.includes('..') || 
      trimmedEmail.startsWith('.') || 
      trimmedEmail.endsWith('.') ||
      trimmedEmail.includes('@.') ||
      trimmedEmail.includes('.@')) {
    return false;
  }
  
  return emailRegex.test(trimmedEmail);
};
